import React, { useState, useEffect } from "react";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { getReportTypeCodeList } from "data/common.data";
import { getCurrentLangName } from "helpers/String.helper";
import { reportComment, reportDiary } from "api/koach/v2/diary.api";

interface ReportPopupProps {
    type: 'diary' | 'comment';
    diaryId: string;
    commentId?: string;
    content: string;
    onClose: () => void;
    onSuccess?: () => void;
}

const ReportPopup: React.FC<ReportPopupProps> = ({
    type,
    diaryId,
    commentId,
    content,
    onClose,
    onSuccess
}) => {
    const [reportType, setReportType] = useState<string>("");
    const [detailReason, setDetailReason] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [reportTypes, setReportTypes] = useState<any[]>([]);

    // 신고 사유 옵션들을 가져오기
    useEffect(() => {
        const loadReportTypes = () => {
            try {
                const types = getReportTypeCodeList();
                if (types && Array.isArray(types)) {
                    setReportTypes(types);
                } else {
                    console.warn('신고 사유 목록을 가져올 수 없습니다:', types);
                    // 기본값 설정
                    setReportTypes([
                        { code: "spam", names: { ko: "스팸/광고", en: "Spam/Advertisement" } },
                        { code: "inappropriate", names: { ko: "부적절한 내용", en: "Inappropriate Content" } },
                        { code: "harassment", names: { ko: "괴롭힘/폭력", en: "Harassment/Violence" } },
                        { code: "copyright", names: { ko: "저작권 침해", en: "Copyright Infringement" } },
                        { code: "other", names: { ko: "기타", en: "Other" } }
                    ]);
                }
            } catch (error) {
                console.error('신고 사유 목록 로드 실패:', error);
                // 에러 발생 시 기본값 설정
                setReportTypes([
                    { code: "spam", names: { ko: "스팸/광고", en: "Spam/Advertisement" } },
                    { code: "inappropriate", names: { ko: "부적절한 내용", en: "Inappropriate Content" } },
                    { code: "harassment", names: { ko: "괴롭힘/폭력", en: "Harassment/Violence" } },
                    { code: "copyright", names: { ko: "저작권 침해", en: "Copyright Infringement" } },
                    { code: "other", names: { ko: "기타", en: "Other" } }
                ]);
            }
        };

        loadReportTypes();
    }, []);

    const handleSubmit = async () => {
        if (!reportType.trim()) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "reportPopup.warningChooseReportType") || "신고 사유를 선택해주세요.");
            return;
        }

        setIsSubmitting(true);
        
        try {
            // 신고 API 호출
            if (type === 'comment' && commentId) {
                // 코멘트 신고
                await reportComment(diaryId, commentId, reportType, detailReason.trim());
            } else {
                // 다이어리 신고
                await reportDiary(diaryId, reportType, detailReason.trim());
            }
            
            // 성공 시 콜백 호출 및 팝업 닫기
            if (onSuccess) {
                onSuccess();
            }
            onClose();
        } catch (error) {
            console.error('신고 제출 실패:', error);
            alert(getLanguageValue(PAGE_TYPE.COMMON, "reportPopup.txtReportError") || '신고 제출에 실패했습니다. 다시 시도해주세요.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (isSubmitting) return; // 제출 중에는 닫기 방지
        onClose();
    };

    const getTitle = () => {
        if (type === 'diary') {
            return getLanguageValue(PAGE_TYPE.COMMON, "reportPopup.txtReportDiaryTitle") || "다이어리 신고";
        }
        return getLanguageValue(PAGE_TYPE.COMMON, "reportPopup.txtReportCommentTitle") || "코멘트 신고";
    };

    const getContentLabel = () => {
        if (type === 'diary') {
            return getLanguageValue(PAGE_TYPE.COMMON, "reportPopup.txtReportDiaryMessage") || "신고할 다이어리:";
        }
        return getLanguageValue(PAGE_TYPE.COMMON, "reportPopup.txtReportCommentMessage") || "신고할 코멘트:";
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {getTitle()}
                    </h3>
                    <button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* 신고할 내용 미리보기 */}
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">
                        {getContentLabel()}
                    </p>
                    <p className="text-sm text-gray-800 overflow-hidden" style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                    }}>
                        {content.length > 100 
                            ? `${content.substring(0, 100)}...` 
                            : content
                        }
                    </p>
                </div>

                {/* 신고 사유 선택 */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {getLanguageValue(PAGE_TYPE.COMMON, "reportPopup.txtReportType") || "신고 사유"}
                    </label>
                    <select
                        value={reportType}
                        onChange={(e) => setReportType(e.target.value)}
                        disabled={isSubmitting}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CF3688] focus:border-[#CF3688] disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                        <option value="">
                            {getLanguageValue(PAGE_TYPE.COMMON, "reportPopup.txtReportTypeSelect") || "신고 사유를 선택해주세요."}
                        </option>
                        {reportTypes.map((type) => (
                            <option key={type.code} value={type.code}>
                                {getCurrentLangName(type.names) || type.code}
                            </option>
                        ))}
                    </select>
                </div>

                {/* 상세 사유 입력 */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {getLanguageValue(PAGE_TYPE.COMMON, "reportPopup.txtDetailReason") || "신고 내용 (선택사항)"}
                    </label>
                    <textarea
                        value={detailReason}
                        onChange={(e) => setDetailReason(e.target.value)}
                        disabled={isSubmitting}
                        maxLength={200}
                        rows={3}
                        placeholder={getLanguageValue(PAGE_TYPE.COMMON, "reportPopup.placeholderInputDetailReason") || "신고 사유를 최대 200자 이내로 작성해주세요."}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CF3688] focus:border-[#CF3688] disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
                    />
                    <div className="text-right text-xs text-gray-500 mt-1">
                        {detailReason.length}/200
                    </div>
                </div>

                {/* 버튼 */}
                <div className="flex gap-3">
                    <button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {getLanguageValue(PAGE_TYPE.COMMON, "reportPopup.btnCancel") || "취소"}
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!reportType.trim() || isSubmitting}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? (
                            <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                <span>
                                    {getLanguageValue(PAGE_TYPE.COMMON, "reportPopup.txtSubmitting") || "제출 중..."}
                                </span>
                            </div>
                        ) : (
                            getLanguageValue(PAGE_TYPE.COMMON, "reportPopup.btnReport") || "신고"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReportPopup;
